import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  useSessionRuns,
  markSeen,
  dismissRun,
  requestKill,
  type WFRunState,
  type WFNodeState,
} from '../state/workflowProgressStore';
import {
  ANTHROPIC_SDK_CAPABILITIES,
  WsEvent,
  isTerminalRunPhase,
  deriveToolTimeline,
  type RunPhase,
  type NodeStatus,
  type ToolTimeline,
  type WorkflowEngineCapabilities,
  type WorkflowUsage,
} from '@voice-assistant/shared';
import { emitWorkflowControl } from '../state/workflowControlWire';

/**
 * AgentRunsPanel — the Agent Runs surface (agent-runs-visualization.md §4).
 *
 * Renderer-only over `workflowProgressStore`. It NEVER synthesizes an outcome: the
 * run's `phase` is the single source of the verdict, and `summary` renders as CONTEXT
 * beneath it — a summary can read as success on a failed run, so it must never be the
 * thing the user reads as the result.
 *
 * Mounted by BOTH shells (mobile: DrawerHost 'runs' sheet; desktop: WorkSurface 'Runs'
 * tab), so it lives firewall-neutral in `components/` and imports from neither.
 *
 * Affordances (Kill/Resume) are rendered from the ENGINE'S CAPABILITIES, never from the
 * engine id — the whole point of the §1 port. The SDK engine reports canKill:true
 * (Query.stopTask + the post-turn finalizer); canResume stays false, so Resume simply
 * does not exist. Kill/Dismiss each fire the LOCAL store intent (requestKill/dismissRun —
 * phase moves only on the server's run_finished) AND the C→S control frame via the
 * workflowControlWire bridge (the store itself stays wire-free, R-A2).
 */

const PHASE_LABEL: Record<RunPhase, string> = {
  queued: 'Queued',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
  failed: 'Failed',
  killed: 'Killed',
  stale: 'Stale',
};

const NODE_GLYPH: Record<NodeStatus, string> = {
  pending: '○', // ○
  running: '◐', // ◐
  retrying: '↻', // ↻
  waiting: '⏸', // ⏸
  completed: '✓', // ✓
  failed: '✗', // ✗
  skipped: '–', // –
};

function fmtTokens(n?: number): string | null {
  if (!n) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function UsageRollup({ usage }: { usage?: WorkflowUsage }) {
  if (!usage) return null;
  const inTok = fmtTokens(usage.inputTokens);
  const outTok = fmtTokens(usage.outputTokens);
  const cost = usage.costUsd != null ? `$${Number(usage.costUsd).toFixed(4)}` : null;
  if (!inTok && !outTok && !cost) return null;
  return (
    <div class="ar-usage">
      {inTok && <span class="ar-usage-item">↓ {inTok}</span>}
      {outTok && <span class="ar-usage-item">↑ {outTok}</span>}
      {cost && <span class="ar-usage-item ar-usage-cost">{cost}</span>}
    </div>
  );
}

/** One compact "name · ↓in ↑out · $cost" chip for the provider/model strips. */
function AttributionChip({ name, usage }: { name: string; usage: WorkflowUsage }) {
  const inTok = fmtTokens(usage.inputTokens);
  const outTok = fmtTokens(usage.outputTokens);
  const cost = usage.costUsd != null ? `$${Number(usage.costUsd).toFixed(4)}` : null;
  return (
    <span class="ar-attr-chip" title={name}>
      <span class="ar-attr-name">{name}</span>
      {inTok && <span class="ar-attr-part">↓ {inTok}</span>}
      {outTok && <span class="ar-attr-part">↑ {outTok}</span>}
      {cost && <span class="ar-attr-part ar-usage-cost">{cost}</span>}
    </span>
  );
}

/**
 * Per-provider + per-model strip (§6.2 step 3). Renders the run's byProvider/byModel
 * DECOMPOSITION — never summed back into `run.usage` (that stays the authoritative rollup
 * above). Gated on the ENGINE's hasModelAttribution capability, never on the engine id.
 */
function ProviderModelStrip({ run }: { run: WFRunState }) {
  const providers = Object.entries(run.byProvider ?? {}).filter(([, u]) => !!u) as [string, WorkflowUsage][];
  const models = Object.entries(run.byModel ?? {}).filter(([, u]) => !!u) as [string, WorkflowUsage][];
  if (providers.length === 0 && models.length === 0) return null;
  return (
    <div class="ar-attr-strip">
      {providers.length > 0 && (
        <div class="ar-attr-row" aria-label="Usage by provider">
          {providers.map(([provider, u]) => (
            <AttributionChip key={`p:${provider}`} name={provider} usage={u} />
          ))}
        </div>
      )}
      {models.length > 0 && (
        <div class="ar-attr-row" aria-label="Usage by model">
          {models.map(([modelId, u]) => (
            <AttributionChip key={`m:${modelId}`} name={modelId} usage={u} />
          ))}
        </div>
      )}
    </div>
  );
}

/** How many tool chips render before collapsing into a "+n" overflow chip. */
const MAX_TOOL_CHIPS = 4;

/** Per-node tool-call chips (§6.2 step 3) — cumulative counts, most-used first. */
function ToolChips({ counts }: { counts?: Record<string, number> }) {
  if (!counts) return null;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (sorted.length === 0) return null;
  const shown = sorted.slice(0, MAX_TOOL_CHIPS);
  const overflow = sorted.length - shown.length;
  return (
    <span class="ar-tool-chips">
      {shown.map(([tool, n]) => (
        <span key={tool} class="ar-tool-chip" title={`${tool}: ${n} call${n === 1 ? '' : 's'}`}>
          {tool}×{n}
        </span>
      ))}
      {overflow > 0 && <span class="ar-tool-chip ar-tool-chip--more">+{overflow}</span>}
    </span>
  );
}

/**
 * Drill-in tool timeline (§6.2 step 4) — fetched from the DURABLE `wf:events` replay endpoint
 * on expand, never from the live store (detail stays off the live socket by design). Renders
 * the explicit #M1 truncation marker when the stream head was trimmed at EVENTS_MAXLEN —
 * a partial history must never present itself as complete.
 */
function RunTimeline({ runId }: { runId: string }) {
  const [state, setState] = useState<
    | { phase: 'loading' }
    | { phase: 'error' }
    | { phase: 'ready'; timeline: ToolTimeline; serverTruncated: boolean }
  >({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch(`/api/workflow/runs/${encodeURIComponent(runId)}/events`);
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { events?: unknown[]; truncated?: boolean } | unknown[];
        const events = Array.isArray(body) ? body : (body?.events ?? []);
        const serverTruncated = !Array.isArray(body) && body?.truncated === true;
        if (!cancelled) setState({ phase: 'ready', timeline: deriveToolTimeline(events), serverTruncated });
      } catch {
        if (!cancelled) setState({ phase: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (state.phase === 'loading') return <div class="ar-timeline ar-timeline--note">Loading timeline…</div>;
  if (state.phase === 'error') return <div class="ar-timeline ar-timeline--note">Timeline unavailable.</div>;

  const { timeline, serverTruncated } = state;
  const truncated = serverTruncated || timeline.truncated;
  const fmtAt = (at: string) => (at ? at.slice(11, 19) : '');
  return (
    <div class="ar-timeline">
      {truncated && (
        <div class="ar-timeline-truncated" role="note">
          Timeline truncated at {timeline.totalEvents} events — older history was trimmed.
        </div>
      )}
      {timeline.entries.length === 0 && <div class="ar-timeline--note">No timeline events recorded.</div>}
      {timeline.entries.map((e, i) => (
        <div key={i} class={`ar-timeline-row ar-timeline-row--${e.kind}`}>
          <span class="ar-timeline-at">{fmtAt(e.at)}</span>
          {e.kind === 'tool' && (
            <span class="ar-timeline-main">
              {e.toolName}
              {e.elapsedSeconds != null ? ` (${e.elapsedSeconds}s)` : ''}
              {e.nodeId ? <span class="ar-timeline-node"> · {e.nodeId}</span> : null}
            </span>
          )}
          {e.kind === 'run_started' && <span class="ar-timeline-main">run started</span>}
          {e.kind === 'run_finished' && <span class="ar-timeline-main">run {e.status ?? 'finished'}</span>}
          {e.kind === 'node_started' && <span class="ar-timeline-main">agent started{e.label ? `: ${e.label}` : ''}</span>}
          {e.kind === 'node_finished' && <span class="ar-timeline-main">agent {e.outcome ?? 'finished'}{e.nodeId ? ` · ${e.nodeId}` : ''}</span>}
        </div>
      ))}
    </div>
  );
}

/** One node row — a phase root or an agent/step under it. `showMeters` is the ENGINE's
 *  hasPerNodeUsage capability (never the engine id) — without it, per-node usage/tool chips
 *  simply do not exist (absence means UNKNOWN, never a zero — SE-COST-06). */
function NodeRow({ node, depth, showMeters }: { node: WFNodeState; depth: number; showMeters?: boolean }) {
  const retrying = node.status === 'retrying' && node.attempt != null;
  return (
    <div class={`ar-node ar-node--${node.status}`} style={{ paddingLeft: `${depth * 14}px` }}>
      <span class={`ar-node-glyph ar-node-glyph--${node.status}`} aria-hidden="true">
        {NODE_GLYPH[node.status]}
      </span>
      <span class="ar-node-label">
        {node.label}
        {/* `partial` is a FLAG on a completed node, never a status of its own. */}
        {node.partial && <span class="ar-node-partial" title="Partial result">◐</span>}
      </span>
      {node.subagentType && <span class="ar-node-type">{node.subagentType}</span>}
      {node.modelId && <span class="ar-node-model" title={`Ran on ${node.modelId}`}>{node.modelId}</span>}
      {retrying && (
        <span class="ar-node-attempt">
          attempt {node.attempt}
          {node.maxAttempts ? `/${node.maxAttempts}` : ''}
        </span>
      )}
      {node.status === 'waiting' && node.waitKind && (
        <span class="ar-node-wait">waiting: {node.waitKind}</span>
      )}
      {/* A single ticker per node — the LAST tool, not a scrolling log. */}
      {node.status === 'running' && node.lastToolName && (
        <span class="ar-node-tool">{node.lastToolName}</span>
      )}
      {/* Per-node meters (§6.2 step 3): cumulative server-owned usage + tool-call chips. */}
      {showMeters && node.usage && (
        <span class="ar-node-usage">
          {fmtTokens(node.usage.inputTokens) && `↓ ${fmtTokens(node.usage.inputTokens)} `}
          {fmtTokens(node.usage.outputTokens) && `↑ ${fmtTokens(node.usage.outputTokens)} `}
          {node.usage.costUsd != null && `$${Number(node.usage.costUsd).toFixed(4)}`}
        </span>
      )}
      {showMeters && <ToolChips counts={node.toolCallCounts} />}
      {node.error && <span class="ar-node-error" title={node.error}>{node.error}</span>}
    </div>
  );
}

/**
 * Group nodes into phase roots + their children. Nodes with no parent that are not
 * phases render at the top level too (an engine may emit a flat run with no phases —
 * the SDK does exactly this when `meta.phases` is absent).
 */
function useRunTree(run: WFRunState) {
  return useMemo(() => {
    const all = Object.values(run.nodes);
    const byOrder = (a: WFNodeState, b: WFNodeState) =>
      (a.orderIndex ?? 0) - (b.orderIndex ?? 0) || (a.childIndex ?? 0) - (b.childIndex ?? 0);
    const roots = all.filter((n) => !n.parentNodeId).sort(byOrder);
    const childrenOf = (id: string) =>
      all.filter((n) => n.parentNodeId === id).sort(byOrder);
    return { roots, childrenOf };
  }, [run.nodes]);
}

/** How long one Kill click disables the button before the user may retry. */
const KILL_RETRY_MS = 15_000;

function RunCard({
  run,
  capabilities,
}: {
  run: WFRunState;
  capabilities: WorkflowEngineCapabilities;
}) {
  const { roots, childrenOf } = useRunTree(run);
  const terminal = isTerminalRunPhase(run.phase);

  // Kill must stay RETRYABLE: `killRequested` alone used to hard-disable the button, so a
  // failed stopTask or a lost task_notification left "stopping…" with no recourse forever.
  // Debounce locally instead — the intent chip still renders off `killRequested`, but the
  // button re-enables after KILL_RETRY_MS so the user can re-request (the server-side kill
  // path is idempotent and the phase still moves ONLY on run_finished:killed).
  const [killDebounce, setKillDebounce] = useState(false);
  useEffect(() => {
    if (!killDebounce) return;
    const t = setTimeout(() => setKillDebounce(false), KILL_RETRY_MS);
    return () => clearTimeout(t);
  }, [killDebounce]);

  // Drill-in timeline (§6.2 step 4) — fetched lazily on expand, from the durable replay
  // endpoint. Gated on the ENGINE's hasToolTimeline capability, never the engine id.
  const [timelineOpen, setTimelineOpen] = useState(false);

  return (
    <div class={`ar-card ar-card--${run.phase}`}>
      <div class="ar-card-head">
        <span class="ar-run-name">{run.name}</span>
        {run.variant && <span class="ar-run-variant">{run.variant}</span>}
        <span class={`ar-phase-chip ar-phase-chip--${run.phase}`}>
          {PHASE_LABEL[run.phase]}
          {/* partial is a flag on completed — surfaced, never promoted to a phase. */}
          {run.phase === 'completed' && run.partial ? ' (partial)' : ''}
        </span>
        {/* The user asked to stop; the phase moves only when the ENGINE says killed. */}
        {run.killRequested && !terminal && <span class="ar-stopping">stopping…</span>}
      </div>

      {run.phase === 'stale' && (
        <div class="ar-stale-note">
          Progress for this run is no longer being received
          {run.staleReason === 'rehydrate' ? ' (restored from a previous session).' : '.'}
        </div>
      )}

      <div class="ar-nodes">
        {roots.map((root) => (
          <div key={root.nodeId} class="ar-phase-group">
            <NodeRow node={root} depth={0} showMeters={capabilities.hasPerNodeUsage} />
            {childrenOf(root.nodeId).map((child) => (
              <NodeRow key={child.nodeId} node={child} depth={1} showMeters={capabilities.hasPerNodeUsage} />
            ))}
          </div>
        ))}
        {roots.length === 0 && <div class="ar-empty-nodes">No steps reported yet.</div>}
      </div>

      <UsageRollup usage={run.usage} />
      {/* Per-provider/per-model strip (§6.2 step 3) — capability-gated, never engine-id-gated. */}
      {capabilities.hasModelAttribution && <ProviderModelStrip run={run} />}

      {/* CONTEXT, never the verdict — the phase chip above is the verdict. */}
      {run.summary && <div class="ar-summary">{run.summary}</div>}

      {capabilities.hasToolTimeline && timelineOpen && <RunTimeline runId={run.runId} />}

      <div class="ar-card-actions">
        {capabilities.hasToolTimeline && (
          <button
            type="button"
            class="ar-btn"
            aria-expanded={timelineOpen}
            onClick={() => setTimelineOpen((v) => !v)}
          >
            {timelineOpen ? 'Hide timeline' : 'Timeline'}
          </button>
        )}
        {capabilities.canKill && !terminal && (
          <button
            type="button"
            class="ar-btn ar-btn--kill"
            disabled={killDebounce}
            onClick={() => {
              // Local intent ("stopping…") + the C→S request. Phase moves ONLY on
              // run_finished:killed — never synthesized client-side. Debounced, not
              // permanently disabled — see killDebounce above.
              setKillDebounce(true);
              requestKill(run.runId);
              emitWorkflowControl(WsEvent.WORKFLOW_KILL, { runId: run.runId });
            }}
          >
            Kill
          </button>
        )}
        {terminal && (
          <button
            type="button"
            class="ar-btn"
            onClick={() => {
              // Local removal + server-side acknowledge (omitted from future snapshots).
              dismissRun(run.runId);
              emitWorkflowControl(WsEvent.WORKFLOW_DISMISS, { runId: run.runId });
            }}
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

export function AgentRunsPanel({ sessionId }: { sessionId: string | null }) {
  const runs = useSessionRuns(sessionId);

  // Capabilities come from the ENGINE, over the wire. The SDK constant is only the
  // pre-fetch default so the first paint doesn't flash controls it then retracts.
  const [capabilities, setCapabilities] = useState<WorkflowEngineCapabilities>(
    ANTHROPIC_SDK_CAPABILITIES,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch('/api/workflow/capabilities');
        if (!res.ok) return; // 403 for non-admin — keep the conservative default
        const body = (await res.json()) as { capabilities?: WorkflowEngineCapabilities };
        if (!cancelled && body?.capabilities) setCapabilities(body.capabilities);
      } catch {
        /* keep the default — never synthesize an affordance we couldn't confirm */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Opening the panel IS the "seen" event — it clears the completed-unseen badge.
  // Runs are keyed by id, so re-running this on the runs list is idempotent.
  useEffect(() => {
    for (const r of runs) {
      if (isTerminalRunPhase(r.phase) && r.unseenTerminal) markSeen(r.runId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs.length]);

  if (runs.length === 0) {
    return (
      <div class="ar-panel ar-panel--empty">
        <p>No agent runs in this session.</p>
        <p class="ar-hint">
          Multi-agent workflows launched from this chat (e.g. with <code>ultracode</code>)
          show their phases and subagents here.
        </p>
      </div>
    );
  }

  return (
    <div class="ar-panel">
      {runs.map((run) => (
        <RunCard key={run.runId} run={run} capabilities={capabilities} />
      ))}
    </div>
  );
}
